import { z } from 'zod';
import { ScheduleSchema } from './schedule';

/**
 * Screens (PLATFORM_EXPANSION.md §2.1) — a screen is a named display owned by a
 * tenant, with its own content, schedule and capability URL. Devices and
 * browsers are viewers of a screen. A device with no `screen` relation keeps
 * today's device-deployment behaviour (backward compatible; migrate gradually).
 */

export const SCREEN_PROFILES = ['home', 'frame', 'desk', 'signage', 'kiosk', 'custom'] as const;
export const ScreenProfileSchema = z.enum(SCREEN_PROFILES);
export type ScreenProfile = z.infer<typeof ScreenProfileSchema>;

export const SCREEN_ORIENTATIONS = ['landscape', 'portrait'] as const;
export const ScreenOrientationSchema = z.enum(SCREEN_ORIENTATIONS);
export type ScreenOrientation = z.infer<typeof ScreenOrientationSchema>;

/** Size classes (§2.1). ESP32 ≈ 800×480; tablets 1280×800; TV 1920×1080. */
export const SIZE_CLASSES = ['esp32-800x480', 'tablet-1280x800', 'tv-1920x1080', 'custom'] as const;
export const SizeClassSchema = z.enum(SIZE_CLASSES);
export type SizeClass = z.infer<typeof SizeClassSchema>;

export const SIZE_CLASS_DIMENSIONS: Record<SizeClass, { w: number; h: number }> = {
  'esp32-800x480': { w: 800, h: 480 },
  'tablet-1280x800': { w: 1280, h: 800 },
  'tv-1920x1080': { w: 1920, h: 1080 },
  custom: { w: 0, h: 0 }
};

export const SCREEN_STATUSES = ['active', 'paused', 'archived'] as const;
export const ScreenStatusSchema = z.enum(SCREEN_STATUSES);
export type ScreenStatus = z.infer<typeof ScreenStatusSchema>;

/**
 * Capability-URL modes (§2.2). `off` serves no public URL; `public` serves
 * without a PIN (D5a: `/s/<id>` is available on all plans); `pin` requires the
 * PIN once and sets a cookie.
 */
export const URL_MODES = ['off', 'public', 'pin'] as const;
export const UrlModeSchema = z.enum(URL_MODES);
export type UrlMode = z.infer<typeof UrlModeSchema>;

/**
 * A screen's capability-URL settings. `public_id` is 128-bit random (≥22 chars
 * of base64url ≈ 132 bits; a 32-hex id also passes); it is unguessable and
 * rotated/revoked at any time. Media is served to a URL viewer only through the
 * screen's own token and only when the current schedule references it.
 */
export const ScreenUrlSchema = z.object({
  url_mode: UrlModeSchema,
  public_id: z.string().min(22).max(128).regex(/^[A-Za-z0-9_-]+$/),
  /** Set only when url_mode = 'pin'. Never returned to a viewer; write-only. */
  url_pin_hash: z.string().min(1).optional(),
  /** Optional expiry; after this instant the URL 404s until rotated. */
  url_expires_at: z.string().datetime().optional(),
  /** false after a rotate/revoke; the URL 404s immediately. */
  url_active: z.boolean().default(true)
});
export type ScreenUrl = z.infer<typeof ScreenUrlSchema>;

export const ScreenSchema = z.object({
  tenant: z.string().min(1),
  name: z.string().min(1).max(120),
  profile: ScreenProfileSchema,
  orientation: ScreenOrientationSchema,
  size_class: SizeClassSchema,
  /** Present once a URL has been minted; absent for a screen with url_mode 'off'. */
  url: ScreenUrlSchema.optional(),
  default_playlist: z.string().min(1).optional(),
  /** The 7-day schedule (§2.3). */
  schedule: ScheduleSchema.optional(),
  timezone: z.string().min(1),
  status: ScreenStatusSchema
});
export type Screen = z.infer<typeof ScreenSchema>;

/** Screen → device attachment (devices.screen relation; §2.1). */
export const ScreenDeviceAttachSchema = z.object({
  screen: z.string().min(1),
  device: z.string().min(1)
});
export type ScreenDeviceAttach = z.infer<typeof ScreenDeviceAttachSchema>;
